import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BillingDocumentPrintSheet,
  printBillingDocument,
} from "@/components/billing/BillingDocumentPrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  BillingInsurerHubError,
  convertProformaToInvoice,
  issueInvoiceDirect,
  issueProforma,
  insurerHubReadyForDocument,
  listBillingDocuments,
  syncBillingDocumentPayerFromPatient,
  updateBillingDocumentPayer,
} from "@/core/billing/billing-documents/billing-documents-store";
import {
  BILLING_DOC_STATUS_LABELS,
  PAYER_TYPE_LABELS,
  type BillingDocument,
  type BillingDocumentSource,
  type PayerType,
} from "@/core/billing/billing-documents/types";
import { BILLING_SOURCE_LABELS, BILLING_SOURCE_OPTIONS } from "@/core/billing/billing-source-labels";
import { getActivePartnershipForInsurer } from "@/core/insurance/partnership-store";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { formatMoney } from "@/core/pharmacy/format";
import { routes } from "@/router/all_routes";

export function BillingAccountingPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selected, setSelected] = useState<BillingDocument | null>(null);
  const [payerType, setPayerType] = useState<PayerType>("patient");
  const [msg, setMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<BillingDocumentSource | "">("");

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:billing-documents", refresh);
    return () => window.removeEventListener("hospitol:billing-documents", refresh);
  }, []);

  const documents = useMemo(() => {
    void refreshKey;
    let list = listBillingDocuments();
    if (sourceFilter) list = list.filter((d) => d.source === sourceFilter);
    return list;
  }, [refreshKey, sourceFilter]);

  const pagination = useClientPagination(documents, [sourceFilter], 25);
  const { pagedItems } = pagination;

  const refresh = () => setRefreshKey((k) => k + 1);

  const selectedPatient = useMemo(() => {
    if (!selected) return null;
    return getPatientById(selected.patientId);
  }, [selected]);

  const patientInsurerLinked = Boolean(selectedPatient?.insurerId);
  const insurerPartnershipActive = Boolean(
    selectedPatient?.insurerId &&
      getActivePartnershipForInsurer(selectedPatient.insurerId),
  );

  const insurerHubReady = selected ? insurerHubReadyForDocument(selected).ready : true;

  const openDoc = (doc: BillingDocument) => {
    const synced = syncBillingDocumentPayerFromPatient(doc.id) ?? doc;
    setSelected(synced);
    setPayerType(synced.payerType);
    setMsg(null);
    setErrorMsg(null);
  };

  const handlePayerSave = () => {
    if (!selected) return;
    if (payerType === "insurer" && selectedPatient?.insurerId) {
      const check = insurerHubReadyForDocument({
        ...selected,
        payerType: "insurer",
        insurerId: selectedPatient.insurerId,
      });
      if (!check.ready) {
        setErrorMsg(check.reason ?? "Partenariat hub requis.");
        return;
      }
    }
    const updated = updateBillingDocumentPayer(selected.id, payerType);
    if (!updated) {
      setErrorMsg(
        payerType === "insurer"
          ? "Payeur assureur impossible — activez d'abord le partenariat hub."
          : "Impossible de définir ce payeur — vérifiez la couverture du patient.",
      );
      return;
    }
    openDoc(updated);
    refresh();
    setMsg("Payeur mis à jour.");
  };

  const runAction = async (action: () => Promise<BillingDocument | null> | BillingDocument | null) => {
    if (!selected) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const updated = await action();
      setBusy(false);
      if (updated) {
        openDoc(updated);
        refresh();
        const hubNote =
          updated.payerType === "insurer" && updated.hubPublishedAt
            ? " Publiée sur le hub assureur."
            : "";
        setMsg(
          `Document ${updated.number} — ${BILLING_DOC_STATUS_LABELS[updated.status]}.${hubNote}`,
        );
      }
    } catch (err) {
      setBusy(false);
      setErrorMsg(
        err instanceof BillingInsurerHubError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Opération impossible.",
      );
      refresh();
      if (selected) {
        const latest = listBillingDocuments().find((d) => d.id === selected.id);
        if (latest) openDoc(latest);
      }
    }
  };

  const payerNameDisplay = useMemo(() => {
    if (!selected) return "";
    if (payerType === "patient") {
      return selectedPatient ? patientFullName(selectedPatient) : selected.customer.name;
    }
    if (payerType === "insurer") {
      return selected.payerName;
    }
    return selectedPatient?.coverageName ?? selected.payerName;
  }, [selected, selectedPatient, payerType]);

  return (
    <div>
      <PageHeader
        title="Comptabilité — facturation"
        description="Proforma, factures définitives et tiers payant. Les encaissements se font dans la Caisse."
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.billingCashier} className="btn btn-success btn-sm">
              Caisse
            </Link>
            <Link to={routes.pharmacyOrders} className="btn btn-outline-secondary btn-sm">
              Ordonnances pharmacie
            </Link>
          </div>
        }
      />

      {msg && <div className="alert alert-success py-2">{msg}</div>}
      {errorMsg && <div className="alert alert-warning py-2">{errorMsg}</div>}

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="ph-invoice-sheet">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h3 className="h6 fw-semibold mb-0">Documents à traiter</h3>
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: "14rem" }}
                value={sourceFilter}
                onChange={(e) =>
                  setSourceFilter(e.target.value as BillingDocumentSource | "")
                }
              >
                <option value="">Tous les types</option>
                {BILLING_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {documents.length === 0 ? (
              <p className="text-muted mb-0">
                Aucun dossier — le pharmacien doit valider le stock puis transférer une ordonnance.
              </p>
            ) : (
              <div className="table-responsive ph-diag-table">
                <table className="table table-sm ph-dash-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>N°</th>
                      <th>Type</th>
                      <th>Patient</th>
                      <th>Payeur</th>
                      <th>Statut</th>
                      <th className="text-end">Montant</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((doc) => {
                      const patient = getPatientById(doc.patientId);
                      return (
                        <tr
                          key={doc.id}
                          className={`ph-diag-row${selected?.id === doc.id ? " table-active" : ""}`}
                        >
                          <td data-label="N°" className="ph-inv-badge">
                            {doc.number}
                          </td>
                          <td data-label="Type" className="small text-muted">
                            {BILLING_SOURCE_LABELS[doc.source]}
                          </td>
                          <td data-label="Patient" className="small">
                            {patient ? patientFullName(patient) : doc.customer.name}
                            {doc.source === "consultation" && doc.serviceLabel && (
                              <span className="d-block text-muted">
                                Consultation — {doc.serviceLabel}
                              </span>
                            )}
                          </td>
                          <td data-label="Payeur" className="small">
                            <span className="badge bg-light text-dark border">
                              {PAYER_TYPE_LABELS[doc.payerType]}
                            </span>
                            {doc.payerType !== "patient" && (
                              <span className="d-block">{doc.payerName}</span>
                            )}
                          </td>
                          <td data-label="Statut">
                            <span className="badge bg-secondary">
                              {BILLING_DOC_STATUS_LABELS[doc.status]}
                            </span>
                            {doc.hubPublishedAt && (
                              <span className="d-block badge bg-info mt-1">Hub</span>
                            )}
                          </td>
                          <td data-label="Montant" className="text-end">
                            {formatMoney(doc.subtotal)}
                          </td>
                          <td data-label="Action" className="text-end">
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => openDoc(doc)}
                            >
                              Ouvrir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TablePagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  totalItems={pagination.totalItems}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-5">
          {selected ? (
            <div className="ph-invoice-sheet">
              <h3 className="h6 fw-semibold mb-3">{selected.number}</h3>
              <p className="small text-muted mb-3">
                {selected.lines.length} ligne(s) · {formatMoney(selected.subtotal)}
              </p>

              <div className="mb-3">
                <label className="form-label small fw-semibold" htmlFor="payer-type">
                  Payeur
                </label>
                <select
                  id="payer-type"
                  className="form-select form-select-sm mb-2"
                  value={payerType}
                  onChange={(e) => setPayerType(e.target.value as PayerType)}
                >
                  <option value="patient">{PAYER_TYPE_LABELS.patient}</option>
                  {patientInsurerLinked && (
                    <option value="insurer">{PAYER_TYPE_LABELS.insurer}</option>
                  )}
                  {selectedPatient?.coverageType === "company" && (
                    <option value="company">{PAYER_TYPE_LABELS.company}</option>
                  )}
                </select>

                {payerType === "insurer" && patientInsurerLinked ? (
                  <div className="alert alert-light border py-2 small mb-2">
                    <strong>Assureur lié au patient</strong>
                    <div className="mt-1">{payerNameDisplay}</div>
                    {!insurerPartnershipActive && (
                      <div className="text-warning mt-1">
                        Partenariat hub requis —{" "}
                        <Link to={routes.insurancePartnerships}>configurer le lien</Link> avant
                        d&apos;émettre une facture assureur.
                      </div>
                    )}
                  </div>
                ) : payerType !== "patient" ? (
                  <div className="alert alert-light border py-2 small mb-2">
                    <strong>{PAYER_TYPE_LABELS[payerType]}</strong>
                    <div className="mt-1">{payerNameDisplay}</div>
                  </div>
                ) : null}

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handlePayerSave}
                >
                  Appliquer payeur
                </button>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3 ph-billing-actions">
                {selected.status === "pending" && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={busy}
                      onClick={() => void runAction(() => issueProforma(selected.id))}
                    >
                      Émettre proforma
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busy || (selected.payerType === "insurer" && !insurerHubReady)}
                      onClick={() => void runAction(() => issueInvoiceDirect(selected.id))}
                    >
                      Facture définitive
                    </button>
                  </>
                )}
                {selected.status === "proforma" && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => printBillingDocument(selected)}
                    >
                      Imprimer proforma
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busy || (selected.payerType === "insurer" && !insurerHubReady)}
                      onClick={() => void runAction(() => convertProformaToInvoice(selected.id))}
                    >
                      Convertir en facture
                    </button>
                  </>
                )}
                {selected.status === "issued" && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => printBillingDocument(selected)}
                  >
                    Imprimer facture
                  </button>
                )}
                {selected.payerType !== "insurer" &&
                  ["pending", "proforma", "at_cashier", "issued"].includes(selected.status) && (
                    <Link
                      to={routes.billingCashier}
                      className="btn btn-success btn-sm"
                    >
                      Envoyer en caisse
                    </Link>
                  )}
              </div>

              <div className="border rounded p-2 bg-light">
                <BillingDocumentPrintSheet document={selected} />
              </div>
            </div>
          ) : (
            <div className="ph-invoice-sheet">
              <p className="text-muted mb-0 small">
                Sélectionnez un document pour émettre une proforma ou une facture définitive.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
